import { Box, TextField, Typography, Link } from "@mui/material";
import React, { useEffect } from "react";
import SyncFrequencySelect from "../components/SyncFrequencySelect";

interface AtlassianIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    syncFrequency?: string;
    domain?: string;
    email?: string;
    apiToken?: string;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: { syncFrequency: string; domain: string; email: string; apiToken: string }) => void;
}

export default function AtlassianIntegrationForm({
  editMode = false,
  initialData = {},
  onValidationChange,
  onDataChange,
}: AtlassianIntegrationFormProps) {
  const [syncFrequency, setSyncFrequency] = React.useState(
    initialData.syncFrequency || "manual"
  );
  const [domain, setDomain] = React.useState(
    initialData.domain || ""
  );
  const [email, setEmail] = React.useState(
    initialData.email || ""
  );
  const [apiToken, setApiToken] = React.useState(
    initialData.apiToken || ""
  );

  // Update parent with current data
  useEffect(() => {
    onDataChange({
      syncFrequency,
      domain,
      email,
      apiToken,
    });
  }, [syncFrequency, domain, email, apiToken, onDataChange]);

  // Validate that all required fields are provided
  useEffect(() => {
    const isValid = domain.trim().length > 0 && 
                   email.trim().length > 0 && 
                   apiToken.trim().length > 0;
    onValidationChange(isValid);
  }, [domain, email, apiToken, onValidationChange]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        To connect Confluence, you'll need to create an API token in your Atlassian account.{" "}
        <Link 
          href="https://id.atlassian.com/manage-profile/security/api-tokens" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          Create an API token
        </Link>
      </Typography>
      
      <TextField
        fullWidth
        label="Confluence Domain"
        placeholder="yourcompany.atlassian.net"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        required
        helperText="Enter your Confluence domain (without https://)"
        sx={{ mb: 2 }}
      />
      
      <TextField
        fullWidth
        label="Email Address"
        placeholder="user@yourcompany.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        helperText="The email address associated with your Atlassian account"
        sx={{ mb: 2 }}
        type="email"
      />
      
      <TextField
        fullWidth
        label="API Token"
        placeholder="Enter your Atlassian API token"
        value={apiToken}
        onChange={(e) => setApiToken(e.target.value)}
        required
        helperText="Your Atlassian API token"
        sx={{ mb: 2 }}
        type="password"
      />
      
      <SyncFrequencySelect
        value={syncFrequency}
        onChange={setSyncFrequency}
        sx={{ mt: 2 }}
      />
    </Box>
  );
}
